import React from 'react'
import { Helmet } from 'react-helmet'
import { isTutor } from '@gopeerproject/chuck'

import { redirectToCore } from '@helpers'
import { useAuth } from '@hooks'

import { Classroom as ClassroomConstants } from '../../../constants'
import {
  useClassroomDispatch,
  useClassroomManager,
  useClassroomQuery,
  useClassroomState
} from '../hooks'
import Header from './Header'
import ConnectionBanner from './ConnectionBanner'
import ExpirationBanner from './Notifications/ExpirationBanner'
import Onboarding from './Onboarding'
import Main from './Main'
import Footer from './Footer'
import Feedback from './Feedback'
import SettingsModal from './Modals/SettingsModal'
import FullscreenModal from './Modals/FullscreenModal'
import ScreenShareModal from './Modals/ScreenShareModal'
import { Loader } from '@components/UI'
import {
  createStyleSheet,
  Divider,
  useStyleSheet,
  View
} from '@gopeerproject/ui-kit'
import Conversation from './Conversation'
import { MemberControls } from './MemberControls'
import { StandardControls } from './StandardControls'
import { MemberWaitingBanner } from './Notifications/MemberWaitingBanner'

const Classroom = () => {
  const styles = useStyleSheet(getStyles)
  const { profile } = useAuth()
  const { data, isLoading } = useClassroomQuery()
  const {
    id,
    step,
    notifications,
    isFullscreenModalOpen,
    isScreenShareModalOpen,
    isSidebarOpen,
    sidebarPanel
  } = useClassroomState()
  const dispatch = useClassroomDispatch()
  const { maximize } = useClassroomManager()

  if (isLoading) return <Loader icon full />

  const navigateToDashboard = () => {
    redirectToCore(`/`)
  }

  const navigateToBooking = () => {
    redirectToCore(`/booking`)
  }

  return (
    <>
      <Helmet>
        <meta name='theme-color' content='#1a1b1e' data-react-helmet='true' />
      </Helmet>

      {!!id &&
        [
          ClassroomConstants.STEPS.READY,
          ClassroomConstants.STEPS.COMPLETED
        ].includes(step) && <ConnectionBanner />}
      {step === ClassroomConstants.STEPS.READY && notifications.expiring && (
        <ExpirationBanner />
      )}

      {step === ClassroomConstants.STEPS.READY &&
        isTutor(profile.type) &&
        data.type === 'group' && <MemberWaitingBanner />}

      <Header />

      {step === ClassroomConstants.STEPS.ONBOARDING && <Onboarding />}

      <View style={styles.main}>
        <View style={{ flex: 1 }}>
          {step === ClassroomConstants.STEPS.READY && <Main />}
          {[ClassroomConstants.STEPS.READY].includes(step) && (
            <Footer withControls={step === ClassroomConstants.STEPS.READY} />
          )}
        </View>
        <Divider type='vertical' style={styles.divider} />
        {step === ClassroomConstants.STEPS.READY &&
          isSidebarOpen &&
          sidebarPanel === 'conversation' && (
            <Conversation style={styles.conversation} id={data.thread} />
          )}
        {step === ClassroomConstants.STEPS.READY &&
          isSidebarOpen &&
          sidebarPanel === 'member_controls' && <MemberControls />}
        {step === ClassroomConstants.STEPS.READY &&
          isSidebarOpen &&
          sidebarPanel === 'standard_controls' && <StandardControls />}
      </View>

      {step === ClassroomConstants.STEPS.COMPLETED && (
        <Feedback
          withDuration={isTutor(profile.type) && data.type !== 'group'}
          self={profile}
          exit={navigateToDashboard}
          exitToSessionRequest={navigateToBooking}
          participants={data.participants}
          appointment={{
            _id: id,
            duration: data.duration,
            overtime: data.overtime
          }}
          showClassroomReview={true}
        />
      )}

      <SettingsModal />

      {isFullscreenModalOpen && (
        <FullscreenModal
          close={() => dispatch({ type: 'close_fullscreen_modal' })}
          maximize={() => {
            dispatch({ type: 'close_fullscreen_modal' })
            maximize()
          }}
        />
      )}

      {isScreenShareModalOpen && (
        <ScreenShareModal
          close={() => dispatch({ type: 'close_screen_share_modal' })}
        />
      )}
    </>
  )
}

const getStyles = createStyleSheet(({ color }) => ({
  main: {
    flexDirection: 'row',
    flex: 1
  },
  conversation: {
    width: 360
  },
  divider: {
    backgroundColor: color.bg.v0
  }
}))

export default Classroom
